import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { AbilitySummary } from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityUtils,
    rulesEngineSelectors,
    characterActions,
    CharacterPreferences,
    SpeedInfo,
    RuleData,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';

import { sidebarActions } from '../../../Shared/actions';
import { appEnvSelectors, characterRollContextSelectors } from '../../../Shared/selectors';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../Shared/utils';
import Inspiration from '../../components/Inspiration';
import ProficiencyBonusBox from '../../components/ProficiencyBonusBox';
import SpeedBox from '../../components/SpeedBox';
import { SheetAppState } from '../../typings';
import { HealthSummary } from '../HealthSummary';

interface Props extends DispatchProp {
    speeds: SpeedInfo;
    proficiencyBonus: number;
    preferences: CharacterPreferences;
    abilities: Array<Ability>;
    inspiration: boolean;
    ruleData: RuleData;
    theme: CharacterTheme;
    isReadonly: boolean;
    diceEnabled: boolean;
    characterRollContext: IRollContext;
}
class QuickInfo extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };
    handleProficiencyBonusClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.PROFICIENCY_BONUS));
    };

    handleInspirationLabelClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.INSPIRATION));
    };

    handleSpeedsClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SPEED_MANAGE));
    };

    handleToggleInspiration = (): void => {
        const { inspiration, dispatch } = this.props;

        dispatch(characterActions.inspirationSet(!inspiration));
    };

    handleAbilitySummaryClick = (ability: Ability): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.ABILITY,
                PaneIdentifierUtils.generateAbility(AbilityUtils.getId(ability)),
            ),
        );
    };

    render() {
        const {
            preferences,
            inspiration,
            proficiencyBonus,
            speeds,
            abilities,
            theme,
            isReadonly,
            ruleData,
            diceEnabled,
            characterRollContext,
        } = this.props;

        let inspirationClasses = ['ct-quick-info__inspiration-status'];
        if (inspiration) {
            inspirationClasses.push('ct-quick-info__inspiration-status--active');
        } else {
            inspirationClasses.push('ct-quick-info__inspiration-status--inactive');
        }

        return (
            <div className="ct-quick-info">
                <div className="ct-quick-info__abilities">
                    {abilities.map((ability) => (
                        <div className="ct-quick-info__ability" key={ability.statKey}>
                            <AbilitySummary
                                theme={theme}
                                ability={ability}
                                preferences={preferences}
                                onClick={this.handleAbilitySummaryClick}
                                diceEnabled={diceEnabled}
                                rollContext={characterRollContext}
                            />
                        </div>
                    ))}
                </div>
                <div className="ct-quick-info__box ct-quick-info__box--proficiency">
                    <ProficiencyBonusBox
                        theme={theme}
                        proficiencyBonus={proficiencyBonus}
                        onClick={this.handleProficiencyBonusClick}
                    />
                </div>
                <div className="ct-quick-info__box ct-quick-info__box--speed">
                    <SpeedBox
                        speeds={speeds}
                        preferences={preferences}
                        theme={theme}
                        ruleData={ruleData}
                        onClick={this.handleSpeedsClick}
                    />
                </div>
                <div className="ct-quick-info__inspiration">
                    <Inspiration
                        theme={theme}
                        inspiration={inspiration}
                        onToggle={this.handleToggleInspiration}
                        onClick={this.handleInspirationLabelClick}
                        isInteractive={!isReadonly}
                    />
                </div>
                <div className="ct-quick-info__health">
                    <HealthSummary />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        abilities: rulesEngineSelectors.getAbilities(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        speeds: rulesEngineSelectors.getCurrentCarriedWeightSpeed(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        inspiration: rulesEngineSelectors.getInspiration(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
    };
}

export default connect(mapStateToProps)(QuickInfo);
